// Dependency-free i18n: no external packages to install, compiles with the
// existing toolchain. Mirrors the small slice of the react-i18next API we use
// (useTranslation -> { t, i18n: { language, changeLanguage } }).
import { useSyncExternalStore } from 'react';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import it from './locales/it.json';
import pt from './locales/pt.json';
import el from './locales/el.json';
import sr from './locales/sr.json';
import hr from './locales/hr.json';
import srLatn from './locales/sr-Latn.json';
import fil from './locales/fil.json';
import mk from './locales/mk.json';
import tr from './locales/tr.json';
import hi from './locales/hi.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import nl from './locales/nl.json';
import sv from './locales/sv.json';
import da from './locales/da.json';
import nb from './locales/nb.json';
import fi from './locales/fi.json';
import ca from './locales/ca.json';
import gl from './locales/gl.json';
import is from './locales/is.json';
import cy from './locales/cy.json';
import ga from './locales/ga.json';
import lb from './locales/lb.json';
import eu from './locales/eu.json';

const resources = { en, es, fr, de, it, pt, el, sr, hr, 'sr-Latn': srLatn, fil, mk, tr, hi, zh, ja, nl, sv, da, nb, fi, ca, gl, is, cy, ga, lb, eu };

// Adding a language = drop in a JSON file, import it, and add it here + below.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'el', name: 'Ελληνικά' },
  { code: 'sr', name: 'Српски (ћирилица)' },
  { code: 'sr-Latn', name: 'Srpski (latinica)' },
  { code: 'fil', name: 'Filipino' },
  { code: 'mk', name: 'Македонски' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'zh', name: '中文（简体）' },
  { code: 'ja', name: '日本語' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'sv', name: 'Svenska' },
  { code: 'da', name: 'Dansk' },
  { code: 'nb', name: 'Norsk' },
  { code: 'fi', name: 'Suomi' },
  { code: 'ca', name: 'Català' },
  { code: 'gl', name: 'Galego' },
  { code: 'is', name: 'Íslenska' },
  { code: 'cy', name: 'Cymraeg' },
  { code: 'ga', name: 'Gaeilge' },
  { code: 'lb', name: 'Lëtzebuergesch' },
  { code: 'eu', name: 'Euskara' },
  { code: 'hr', name: 'Hrvatski' },
];

const FALLBACK = 'en';

// Currency is DISPLAY RELABEL ONLY: the numeric amount is never converted
// between currencies (no exchange rates) - choosing a currency simply swaps the
// symbol shown next to the existing number.
//
// Currency is decoupled from language: language only sets a *default* (used when
// the user leaves currency on "Auto"); an explicit pick always wins.
export const SUPPORTED_CURRENCIES = [
  { code: 'AUD', symbol: 'A$',   name: 'Australian Dollar (A$)' },
  { code: 'USD', symbol: '$',    name: 'US Dollar ($)' },
  { code: 'GBP', symbol: '£',    name: 'British Pound (£)' },
  { code: 'EUR', symbol: '€',    name: 'Euro (€)' },
  { code: 'NZD', symbol: 'NZ$',  name: 'New Zealand Dollar (NZ$)' },
  { code: 'CAD', symbol: 'C$',   name: 'Canadian Dollar (C$)' },
  { code: 'CHF', symbol: 'CHF',  name: 'Swiss Franc (CHF)' },
  { code: 'JPY', symbol: '¥',    name: 'Japanese Yen (¥)' },
  { code: 'CNY', symbol: '¥',    name: 'Chinese Yuan (¥)' },
  { code: 'INR', symbol: '₹',    name: 'Indian Rupee (₹)' },
  { code: 'TRY', symbol: '₺',    name: 'Turkish Lira (₺)' },
  { code: 'PHP', symbol: '₱',    name: 'Philippine Peso (₱)' },
  { code: 'BRL', symbol: 'R$',   name: 'Brazilian Real (R$)' },
  { code: 'SEK', symbol: 'kr',   name: 'Swedish Krona (kr)' },
  { code: 'DKK', symbol: 'kr',   name: 'Danish Krone (kr)' },
  { code: 'NOK', symbol: 'kr',   name: 'Norwegian Krone (kr)' },
  { code: 'ISK', symbol: 'kr',   name: 'Icelandic Króna (kr)' },
  { code: 'RSD', symbol: 'дин.', name: 'Serbian Dinar (дин.)' },
  { code: 'MKD', symbol: 'ден',  name: 'Macedonian Denar (ден)' },
  { code: 'KRW', symbol: '₩',    name: 'South Korean Won (₩)' },
  { code: 'PLN', symbol: 'zł',   name: 'Polish Złoty (zł)' },
  { code: 'CZK', symbol: 'Kč',   name: 'Czech Koruna (Kč)' },
  { code: 'HUF', symbol: 'Ft',   name: 'Hungarian Forint (Ft)' },
  { code: 'RUB', symbol: '₽',    name: 'Russian Ruble (₽)' },
  { code: 'ILS', symbol: '₪',    name: 'Israeli Shekel (₪)' },
  { code: 'ZAR', symbol: 'R',    name: 'South African Rand (R)' },
  { code: 'THB', symbol: '฿',    name: 'Thai Baht (฿)' },
  { code: 'AED', symbol: 'AED',  name: 'UAE Dirham (AED)' },
  { code: 'SGD', symbol: 'S$',   name: 'Singapore Dollar (S$)' },
  { code: 'HKD', symbol: 'HK$',  name: 'Hong Kong Dollar (HK$)' },
  { code: 'MYR', symbol: 'RM',   name: 'Malaysian Ringgit (RM)' },
  { code: 'IDR', symbol: 'Rp',   name: 'Indonesian Rupiah (Rp)' },
  { code: 'VND', symbol: '₫',    name: 'Vietnamese Đồng (₫)' },
  { code: 'MXN', symbol: 'MX$',  name: 'Mexican Peso (MX$)' },
  { code: 'ARS', symbol: 'AR$',  name: 'Argentine Peso (AR$)' },
  { code: 'COP', symbol: 'COL$', name: 'Colombian Peso (COL$)' },
  { code: 'CLP', symbol: 'CL$',  name: 'Chilean Peso (CL$)' },
  { code: 'PEN', symbol: 'S/',   name: 'Peruvian Sol (S/)' },
];
const CURRENCY_SYMBOL = SUPPORTED_CURRENCIES.reduce((m, c) => { m[c.code] = c.symbol; return m; }, {});

// Language -> default currency code (only used when currency is on "Auto").
const LANG_DEFAULT_CURRENCY = {
  en: 'USD',
  de: 'EUR', fr: 'EUR', it: 'EUR', es: 'EUR', pt: 'EUR', el: 'EUR', nl: 'EUR',
  ca: 'EUR', gl: 'EUR', eu: 'EUR', ga: 'EUR', lb: 'EUR', fi: 'EUR', hr: 'EUR',
  cy: 'GBP',
  da: 'DKK', sv: 'SEK', nb: 'NOK', is: 'ISK',
  tr: 'TRY', hi: 'INR', ja: 'JPY', zh: 'CNY', fil: 'PHP',
  sr: 'RSD', 'sr-Latn': 'RSD', mk: 'MKD',
};
const FALLBACK_CURRENCY = 'USD';

// Region (ISO 3166 country) -> currency, for detecting the currency from the
// device locale (e.g. en-AU -> AUD) when the user hasn't picked one. Only
// covers currencies the app supports; anything else falls back to the
// language default below. Eurozone countries all map to EUR.
const REGION_DEFAULT_CURRENCY = {
  US: 'USD', AU: 'AUD', GB: 'GBP', NZ: 'NZD', CA: 'CAD', CH: 'CHF',
  JP: 'JPY', CN: 'CNY', IN: 'INR', TR: 'TRY', PH: 'PHP', BR: 'BRL',
  SE: 'SEK', DK: 'DKK', NO: 'NOK', IS: 'ISK', RS: 'RSD', MK: 'MKD',
  KR: 'KRW', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RU: 'RUB', IL: 'ILS',
  ZA: 'ZAR', TH: 'THB', AE: 'AED', SG: 'SGD', HK: 'HKD', MY: 'MYR',
  ID: 'IDR', VN: 'VND', MX: 'MXN', AR: 'ARS', CO: 'COP', CL: 'CLP', PE: 'PEN',
  // Eurozone
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR',
  FI: 'EUR', FR: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR',
  LU: 'EUR', LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR',
  SK: 'EUR', HR: 'EUR',
};

/**
 * The region subtag of a locale tag, or null. "en-AU" -> "AU".
 *
 * WHY THIS IS NOT A ONE-LINER (25 Sep 2026, from Emergent's browser)
 *
 *   It used to be `split('-')` and test the LAST part against two letters.
 *   Emergent's harness reports `navigator.language` as "en-US@posix", and
 *   the last part of that is "US@posix", which is not two letters - so a
 *   locale that plainly states its region was read as having none. The
 *   region step then found nothing and the APP LANGUAGE decided the money:
 *   the same account showed $159 in English and €159 in Spanish, same
 *   figures, currency relabelled rather than converted. That is the v18.16
 *   bug, alive again through a parsing gap rather than the logic it fixed.
 *
 *   Three more tags were failing the same way, and one was failing the
 *   other direction:
 *
 *     en-US@posix          POSIX modifier   -> read as no region
 *     en-US-u-ca-gregory   BCP-47 extension -> read as no region
 *     en_AU                underscore form  -> read as no region
 *     en                   no region at all -> read as region "EN"
 *
 *   The last one is the dangerous shape: "en" has one part, that part is
 *   two letters, so the old rule called the LANGUAGE a region. It never
 *   misfired only because no country is coded EN - an accident, not a
 *   design, and one that a new entry in the table would have ended.
 *
 *   So: cut the POSIX modifier, accept the underscore form, never read the
 *   first subtag as a region, stop at an extension singleton, and skip the
 *   four-letter script subtag.
 */
export function regionOf(tag) {
  if (!tag) return null;
  const cleaned = String(tag).split('@')[0].replace(/_/g, '-');
  const parts = cleaned.split('-').filter(Boolean);
  // parts[0] is the LANGUAGE. A region can never be the first subtag.
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    // "u", "t", "x" open an extension; nothing after one is a region.
    if (part.length === 1) break;
    if (part.length === 4) continue;            // a script subtag, e.g. Latn
    if (/^[A-Za-z]{2}$/.test(part)) return part.toUpperCase();
    if (/^\d{3}$/.test(part)) return part;      // UN M.49, e.g. "419"
  }
  return null;
}

// Best-effort currency from the device locale's region subtag
// (navigator.languages), e.g. "en-AU" -> AUD, "sr-Latn-RS" -> RSD.
function detectRegionCurrency() {
  try {
    const langs = (typeof navigator !== 'undefined' && (navigator.languages && navigator.languages.length
      ? navigator.languages : (navigator.language ? [navigator.language] : []))) || [];
    for (const l of langs) {
      const region = regionOf(l);
      if (!region) continue;
      const code = REGION_DEFAULT_CURRENCY[region];
      if (code) return code;
    }
  } catch (e) { /* noop */ }
  return null;
}

// Explicit override ('' / null = Auto = detect from device region, then language).
let currentCurrency = (() => {
  try { return localStorage.getItem('appCurrency') || ''; } catch (e) { return ''; }
})();

// The language-derived fallback, decided ONCE and then left alone.
const RESOLVED_KEY = 'appCurrencyResolvedFromLang';

// Resolve the active currency code: explicit pick wins, then the device
// region (e.g. Australia -> AUD), then a language default that is frozen the
// first time it is used.
//
// WHY THE FREEZING (21 Sep 2026, found by Emergent's browser test)
//
//     This used to end `return LANG_DEFAULT_CURRENCY[currentLang]`, live, on
//     every call. On any device whose locale carries no country - plain "en"
//     rather than "en-AU" - the region step finds nothing and the APP
//     LANGUAGE decided the money. Switching the app to Espanol relabelled an
//     Australian household's entire spending history from $ to EUR.
//
//     Not converted. RELABELLED. The same 1054 became 1054 euros, and every
//     chart, export and budget went with it. A screenshot of the Spanish
//     Insights tab showed a heading reading EUR 1054 above a sentence
//     reading $1505, which is the same bug seen from the other side.
//
//     Currency is a fact about where you shop. Language is a fact about what
//     you like to read. Letting the second decide the first is wrong even
//     when the guess is right, because the guess CHANGES under the user.
//
//     Device region stays live on purpose - that one really does track where
//     a person is, and somebody who moves to Australia should start seeing
//     AUD. Only the language guess is frozen, so it can be made once and
//     never silently revisited.
export function getCurrencyCode() {
  if (currentCurrency && CURRENCY_SYMBOL[currentCurrency]) return currentCurrency;
  const region = detectRegionCurrency();
  if (region && CURRENCY_SYMBOL[region]) return region;
  try {
    const frozen = localStorage.getItem(RESOLVED_KEY);
    if (frozen && CURRENCY_SYMBOL[frozen]) return frozen;
  } catch (e) { /* storage unavailable - fall through and just resolve */ }
  const guess = LANG_DEFAULT_CURRENCY[currentLang] || FALLBACK_CURRENCY;
  try { localStorage.setItem(RESOLVED_KEY, guess); } catch (e) { /* noop */ }
  return guess;
}
export function getCurrencySetting() {
  // '' means Auto; otherwise the explicit code.
  return currentCurrency && CURRENCY_SYMBOL[currentCurrency] ? currentCurrency : '';
}
export function setCurrency(code) {
  const next = code && CURRENCY_SYMBOL[code] ? code : '';
  currentCurrency = next;
  try {
    if (next) localStorage.setItem('appCurrency', next);
    else localStorage.removeItem('appCurrency');
  } catch (e) { /* noop */ }
  listeners.forEach((l) => l());
}

// Non-hook accessors (for template literals / non-component code).
export function getCurrencySymbol() {
  return CURRENCY_SYMBOL[getCurrencyCode()] || '$';
}
// Symbol for a specific ISO code (e.g. a scanned receipt's currency).
// - Known currency  -> its symbol (€, ฿, CHF…)
// - Valid but unmapped ISO code -> the code itself ("THB 500.00"), never a
//   misleading home-currency symbol
// - Empty/invalid -> null, so callers fall back to the home currency symbol
export function symbolForCurrency(code) {
  if (!code) return null;
  if (CURRENCY_SYMBOL[code]) return CURRENCY_SYMBOL[code];
  if (/^[A-Za-z]{3}$/.test(code)) return code.toUpperCase() + '\u00A0';
  return null;
}
export function formatMoney(amount, decimals = 2) {
  const n = Number(amount);
  const num = isFinite(n) ? n : 0;
  return `${getCurrencySymbol()}${num.toFixed(decimals)}`;
}

function detectInitial() {
  try {
    const saved = localStorage.getItem('appLanguage');
    if (saved && resources[saved]) return saved;
    const nav = (navigator.language || FALLBACK).split('-')[0];
    return resources[nav] ? nav : FALLBACK;
  } catch (e) {
    return FALLBACK;
  }
}

let currentLang = detectInitial();
const listeners = new Set();

function applyHtmlLang() {
  try { document.documentElement.setAttribute('lang', currentLang); } catch (e) { /* noop */ }
}
applyHtmlLang();

function getNested(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

// Fill {{var}} AND {var} placeholders in a template string. Pure + exported
// so it can be unit-tested directly. Tolerates whitespace inside the braces.
//
// Both styles are accepted because the locale files historically mix them:
// older keys use {{name}}, several newer feature blocks (importList, dueNow,
// priceIndex, valueInsights, mealCost, duplicate prompt...) were written with
// single-brace {count}. Only handling {{var}} meant every one of those strings
// rendered its raw placeholder on screen, in all 28 locales. Supporting both
// here fixes them all at once without touching 28 files.
//
// Order matters: the double-brace pass runs first, so {{count}} never gets
// half-eaten by the single-brace pass into a stray "{3}".
export function interpolate(template, vars) {
  if (typeof template !== 'string' || !vars) return template;
  let out = template;
  Object.keys(vars).forEach((k) => {
    out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), vars[k]);
    out = out.replace(new RegExp(`{\\s*${k}\\s*}`, 'g'), vars[k]);
  });
  return out;
}

// Translate a dot-path key, falling back to English, with {{var}} interpolation.
export function t(key, vars) {
  let val = getNested(resources[currentLang], key);
  if (val == null) val = getNested(resources[FALLBACK], key);
  if (val == null) return key;
  // Safety: a caller may pass a BRANCH key (e.g. 'listDetail.basket') that
  // resolves to a nested object instead of a leaf string. Rendering that
  // object as a JSX child throws React error #31 and white-screens the whole
  // page. Never return a non-string - degrade to the key text so a mis-keyed
  // call shows harmless placeholder text instead of taking the app down.
  if (typeof val !== 'string') return key;
  if (vars) {
    val = interpolate(val, vars);
  }
  return val;
}

export function changeLanguage(lng) {
  const next = resources[lng] ? lng : FALLBACK;
  if (next === currentLang) return;
  currentLang = next;
  try { localStorage.setItem('appLanguage', next); } catch (e) { /* noop */ }
  applyHtmlLang();
  listeners.forEach((l) => l());
}

export function getLanguage() {
  return currentLang;
}

/**
 * The tag to format a date, a time or a number with: the app's language,
 * carrying the reader's REGION when the device offers one for that language.
 *
 * WHY THE LANGUAGE ALONE IS NOT ENOUGH (22 September 2026, v18.35)
 *
 *     v18.33 moved every date from the phone's locale to the app's language,
 *     because a Spanish household on an English phone was reading English
 *     months. That was right, and it broke something else the same day:
 *
 *         toLocaleDateString(undefined)   -> 22/09/2026   (an AU phone)
 *         toLocaleDateString('en')        -> 9/22/2026
 *         toLocaleDateString('en-AU')     -> 22/09/2026
 *
 *     A bare language resolves to that language's DEFAULT region, and for
 *     English that is the United States. So the release that fixed the words
 *     reversed the numbers, and on Ivan's own phone - where the old
 *     device-locale behaviour had been right by accident - the Lists page
 *     went American.
 *
 *     The language never carried the region. It is a different question with
 *     a different answer, and "pass the language you already hold" is the
 *     most careful-looking way to get it wrong: the Tradie's Bay fork had
 *     five call sites doing exactly that, counted as correct by both of us,
 *     and 03/09/2026 in a trade app means two different days to the two
 *     people reading it.
 *
 * THE RULE
 *
 *     Language from the app, region from the device - and only from a device
 *     locale whose language MATCHES, so a Spanish app on an en-AU phone does
 *     not end up as "es-AU". Anything else keeps the bare language, which is
 *     the correct answer when there is nothing better to say.
 */
export function readerLocale() {
  const app = String(currentLang || FALLBACK);
  // Already carries a region (a 2-letter or 3-digit subtag after the first).
  const appParts = app.split('-');
  if (appParts.slice(1).some((p) => /^([A-Za-z]{2}|\d{3})$/.test(p))) return app;

  try {
    const langs = (typeof navigator !== 'undefined'
      && (navigator.languages && navigator.languages.length
        ? navigator.languages
        : (navigator.language ? [navigator.language] : []))) || [];
    for (const l of langs) {
      if (!l) continue;
      const parts = String(l).split('-');
      if (parts[0].toLowerCase() !== appParts[0].toLowerCase()) continue;
      const region = parts.slice(1).find((p) => /^([A-Za-z]{2}|\d{3})$/.test(p));
      // The app's tag plus the device's region: "sr-Latn" + RS stays Latin.
      if (region) return `${app}-${region.toUpperCase()}`;
    }
  } catch (e) { /* noop */ }
  return app;
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Hook mirroring react-i18next's shape so call sites stay identical.
export function useTranslation() {
  const language = useSyncExternalStore(subscribe, getLanguage, getLanguage);
  return { t, i18n: { language, changeLanguage } };
}

// Reactive currency hook - re-renders on language OR currency change (shared
// store). Returns the active symbol + resolved code + a format() helper.
export function useCurrency() {
  // Snapshot is the resolved code so a currency change (even with the same
  // language) triggers a re-render.
  const code = useSyncExternalStore(subscribe, getCurrencyCode, getCurrencyCode);
  const symbol = CURRENCY_SYMBOL[code] || '$';
  const format = (amount, decimals = 2) => {
    const n = Number(amount);
    return `${symbol}${(isFinite(n) ? n : 0).toFixed(decimals)}`;
  };
  return { symbol, code, format };
}

export default { t, changeLanguage, getLanguage, useTranslation, useCurrency, getCurrencySymbol, symbolForCurrency, getCurrencyCode, getCurrencySetting, setCurrency, formatMoney, SUPPORTED_LANGUAGES, SUPPORTED_CURRENCIES };
